import { getAuditList } from "../../../../services/workspace";
import dayjs from "dayjs";

/**
 * Adapter del Activity Log para usar el nuevo endpoint de auditoría.
 *
 * Mantiene el contrato actual consumido por el componente `ActivityLog`:
 * retorna un objeto con:
 * - body: array de eventos
 * - meta: { totalItems, totalPages, currentPage, perPage, hasNext, hasPrevious, nextCursor }
 *
 * Los filtros actuales del UI (roles, actions, sections, modified, search) se
 * adaptan a la firma del endpoint `/v1/audit/list`.
 */
export const getActivityLog = async (params = {}) => {
  const page = params.page || 1;
  const pageSize = params.page_size || 20;

  // Mapeo de campos de ordenamiento del UI -> backend
  const orderByMap = {
    timestamp: "created_at",
    userName: "user_full_name",
    action: "action",
    section: "section",
    outcome: "outcome",
  };

  const rawOrderBy = params.order_by || "timestamp";
  const orderBy = Array.isArray(rawOrderBy) ? rawOrderBy[0] : rawOrderBy;
  const mappedOrderBy = orderByMap[orderBy] || "created_at";

  const rawOrderDirection = params.order_direction || "DESC";
  const orderDirection = Array.isArray(rawOrderDirection)
    ? (rawOrderDirection[0] || "DESC").toUpperCase()
    : String(rawOrderDirection).toUpperCase();

  // Normalizar search a { keyword }
  const searchKeyword =
    typeof params.search === "string"
      ? params.search
      : params.search?.keyword;

  const filters = {};

  // Por ahora solo enviamos la acción; otros filtros (roles/sections) requieren UUIDs
  if (Array.isArray(params.actions) && params.actions.length > 0) {
    filters.action = params.actions[0];
  }

  // Agregar filtro de section si viene
  // Nota: El endpoint real espera section_uuid, pero para el mock usamos el valor directo (ej: "CONTACTS", "AUTH")
  // Cuando el endpoint funcione, esto debería cambiarse a section_uuid si el backend lo requiere
  if (Array.isArray(params.sections) && params.sections.length > 0) {
    // Por ahora usamos el valor directo para compatibilidad con el mock
    // TODO: Cambiar a section_uuid cuando el endpoint real esté disponible
    filters.section = params.sections[0];
  }

  // Mapear quick ranges de "Modified" a modified_from / modified_to
  if (params.modified) {
    const now = dayjs();
    let from = null;

    switch (params.modified) {
      case "Last 24 hours":
        from = now.subtract(24, "hour");
        break;
      case "Last 7 days":
        from = now.subtract(7, "day");
        break;
      case "Last 30 days":
        from = now.subtract(30, "day");
        break;
      case "Last 3 months":
        from = now.subtract(3, "month");
        break;
      case "Last year":
        from = now.subtract(1, "year");
        break;
      default:
        from = null;
    }

    if (from) {
      filters.modified_from = from.toISOString();
      filters.modified_to = now.toISOString();
    }
  }

  const requestPayload = {
    page,
    page_size: pageSize,
    order_by: mappedOrderBy,
    order_direction: orderDirection,
  };

  if (searchKeyword && searchKeyword.trim() !== "") {
    requestPayload.search = { keyword: searchKeyword.trim() };
  }

  if (Object.keys(filters).length > 0) {
    requestPayload.filters = filters;
  }

  try {
    const response = await getAuditList(requestPayload);

    const statusCode = response.status_code || response.statusCode || 200;

    if (statusCode < 200 || statusCode >= 300) {
      throw new Error(
        response.message || "Failed to fetch activity log from audit endpoint"
      );
    }

    const body = Array.isArray(response.body) ? response.body : [];
    const meta = response.meta || {};

    return {
      body,
      meta: {
        totalItems: meta.totalItems ?? meta.total_items ?? 0,
        totalPages: meta.totalPages ?? meta.total_pages ?? 0,
        currentPage: meta.currentPage ?? meta.current_page ?? page,
        perPage: meta.perPage ?? meta.per_page ?? pageSize,
        hasNext: meta.hasNext ?? meta.has_next ?? false,
        hasPrevious: meta.hasPrevious ?? meta.has_previous ?? false,
        nextCursor: meta.nextCursor ?? meta.next_cursor ?? null,
      },
    };
  } catch (error) {
    console.warn("API call failed, falling back to mock data:", error.message);
    
    // TODO: REMOVE THIS MOCK FALLBACK WHEN API /v1/audit/list IS STABLE
    try {
      const response = await fetch("/mock-activity-log.json");
      const data = await response.json();

      // Generate dynamic dates based on current time for each time range
      const now = dayjs();
      const mockActivities = [
        // Last 24 hours (3 items)
        {
          user_full_name: "Jane Doe",
          action: "Updated",
          section: "CONTACTS",
          item: "Contact: John Smith",
          description: "Updated primary email from john@old.com to john@new.com",
          outcome: "Success",
          created_at: now.subtract(2, "hour").toISOString(),
        },
        {
          user_full_name: "John Admin",
          action: "Login",
          section: "AUTH",
          item: "User Session",
          description: "User logged in from IP 192.168.1.10",
          outcome: "Success",
          created_at: now.subtract(6, "hour").toISOString(),
        },
        {
          user_full_name: "Emily Johnson",
          action: "Created",
          section: "USERS",
          item: "User: Sarah Garcia",
          description: "Invited new user to workspace",
          outcome: "Success",
          created_at: now.subtract(12, "hour").toISOString(),
        },
        // Last 7 days (3 items)
        {
          user_full_name: "Michael Williams",
          action: "Updated",
          section: "WORKSPACE",
          item: "Workspace Settings",
          description: "Changed workspace name to \"Brightline Auto Group\"",
          outcome: "Success",
          created_at: now.subtract(2, "day").toISOString(),
        },
        {
          user_full_name: "Sarah Garcia",
          action: "Deleted",
          section: "USERS",
          item: "User: James Martinez",
          description: "Removed inactive user from workspace",
          outcome: "Success",
          created_at: now.subtract(4, "day").toISOString(),
        },
        {
          user_full_name: "James Martinez",
          action: "Modified",
          section: "AGENTS",
          item: "Agent: Sales Bot",
          description: "Updated agent configuration and training data",
          outcome: "Success",
          created_at: now.subtract(6, "day").toISOString(),
        },
        // Last 30 days (3 items)
        {
          user_full_name: "Emily Johnson",
          action: "Updated",
          section: "CONTACTS",
          item: "Contact: Alex Thompson",
          description: "Updated contact phone number and email address",
          outcome: "Success",
          created_at: now.subtract(10, "day").toISOString(),
        },
        {
          user_full_name: "Michael Williams",
          action: "Access",
          section: "AUTH",
          item: "User Session",
          description: "Failed login attempt (incorrect password)",
          outcome: "Failed",
          created_at: now.subtract(15, "day").toISOString(),
        },
        {
          user_full_name: "Jane Doe",
          action: "Created",
          section: "AGENTS",
          item: "Agent: Support Bot",
          description: "Created new AI agent for customer support",
          outcome: "Success",
          created_at: now.subtract(20, "day").toISOString(),
        },
        // Last 3 months (3 items)
        {
          user_full_name: "John Smith",
          action: "Updated",
          section: "DRIVES",
          item: "Drive: Marketing Campaign",
          description: "Modified drive parameters for better engagement",
          outcome: "Success",
          created_at: now.subtract(1, "month").toISOString(),
        },
        {
          user_full_name: "Sarah Garcia",
          action: "Deleted",
          section: "DATA",
          item: "Data: Old Customer Records",
          description: "Archived outdated customer data",
          outcome: "Success",
          created_at: now.subtract(2, "month").toISOString(),
        },
        {
          user_full_name: "Emily Johnson",
          action: "Created",
          section: "CONVERSATIONS",
          item: "Conversation: Customer Chat #4523",
          description: "Started new conversation with lead",
          outcome: "Success",
          created_at: now.subtract(2, "month").subtract(15, "day").toISOString(),
        },
        // Last year (3 items)
        {
          user_full_name: "Michael Williams",
          action: "Modified",
          section: "WORKSPACE",
          item: "User Permissions",
          description: "Updated default user permissions for new members",
          outcome: "Success",
          created_at: now.subtract(6, "month").toISOString(),
        },
        {
          user_full_name: "James Martinez",
          action: "Deleted",
          section: "AGENTS",
          item: "Agent: Test Agent",
          description: "Removed test agent after evaluation",
          outcome: "Success",
          created_at: now.subtract(8, "month").toISOString(),
        },
        {
          user_full_name: "Jane Doe",
          action: "Updated",
          section: "CONTACTS",
          item: "Contact: Maria Rodriguez",
          description: "Updated contact information and added notes",
          outcome: "Success",
          created_at: now.subtract(10, "month").toISOString(),
        },
        // Additional items for pagination testing - Last 24 hours
        {
          user_full_name: "Robert Brown",
          action: "Login",
          section: "AUTH",
          item: "User Session",
          description: "User logged in from Chrome browser",
          outcome: "Success",
          created_at: now.subtract(1, "hour").toISOString(),
        },
        {
          user_full_name: "Lisa Anderson",
          action: "Updated",
          section: "CONTACTS",
          item: "Contact: David Wilson",
          description: "Updated contact address information",
          outcome: "Success",
          created_at: now.subtract(3, "hour").toISOString(),
        },
        {
          user_full_name: "Tom Harris",
          action: "Created",
          section: "AGENTS",
          item: "Agent: Customer Service Bot",
          description: "Created new customer service agent",
          outcome: "Success",
          created_at: now.subtract(8, "hour").toISOString(),
        },
        {
          user_full_name: "Anna Taylor",
          action: "Modified",
          section: "DRIVES",
          item: "Drive: Sales Campaign",
          description: "Modified drive targeting parameters",
          outcome: "Success",
          created_at: now.subtract(18, "hour").toISOString(),
        },
        // Additional items for pagination testing - Last 7 days
        {
          user_full_name: "Chris Lee",
          action: "Deleted",
          section: "DATA",
          item: "Data: Archived Records",
          description: "Deleted archived customer records",
          outcome: "Success",
          created_at: now.subtract(1, "day").toISOString(),
        },
        {
          user_full_name: "Maria Garcia",
          action: "Updated",
          section: "USERS",
          item: "User: Robert Brown",
          description: "Updated user role and permissions",
          outcome: "Success",
          created_at: now.subtract(3, "day").toISOString(),
        },
        {
          user_full_name: "David Wilson",
          action: "Created",
          section: "CONVERSATIONS",
          item: "Conversation: Support Ticket #7891",
          description: "Created new support conversation",
          outcome: "Success",
          created_at: now.subtract(5, "day").toISOString(),
        },
        {
          user_full_name: "Sarah Johnson",
          action: "Access",
          section: "AUTH",
          item: "User Session",
          description: "User accessed workspace dashboard",
          outcome: "Success",
          created_at: now.subtract(1, "day").subtract(4, "hour").toISOString(),
        },
        // Additional items for pagination testing - Last 30 days
        {
          user_full_name: "Mark Thompson",
          action: "Updated",
          section: "WORKSPACE",
          item: "Workspace Configuration",
          description: "Updated workspace billing settings",
          outcome: "Success",
          created_at: now.subtract(8, "day").toISOString(),
        },
        {
          user_full_name: "Jennifer White",
          action: "Created",
          section: "AGENTS",
          item: "Agent: Lead Qualification Bot",
          description: "Created new lead qualification agent",
          outcome: "Success",
          created_at: now.subtract(12, "day").toISOString(),
        },
        {
          user_full_name: "Paul Martinez",
          action: "Deleted",
          section: "CONTACTS",
          item: "Contact: Old Lead",
          description: "Removed inactive contact from database",
          outcome: "Success",
          created_at: now.subtract(18, "day").toISOString(),
        },
        {
          user_full_name: "Karen Davis",
          action: "Modified",
          section: "DRIVES",
          item: "Drive: Email Campaign",
          description: "Modified email campaign parameters",
          outcome: "Success",
          created_at: now.subtract(22, "day").toISOString(),
        },
        {
          user_full_name: "Steven Clark",
          action: "Updated",
          section: "USERS",
          item: "User: Team Member",
          description: "Updated user profile information",
          outcome: "Success",
          created_at: now.subtract(25, "day").toISOString(),
        },
        {
          user_full_name: "Amanda Lewis",
          action: "Access",
          section: "AUTH",
          item: "User Session",
          description: "Failed login attempt - wrong password",
          outcome: "Failed",
          created_at: now.subtract(28, "day").toISOString(),
        },
        // Additional items for pagination testing - Last 3 months
        {
          user_full_name: "Kevin Walker",
          action: "Created",
          section: "CONVERSATIONS",
          item: "Conversation: Sales Inquiry",
          description: "Started new sales conversation",
          outcome: "Success",
          created_at: now.subtract(1, "month").subtract(5, "day").toISOString(),
        },
        {
          user_full_name: "Rachel Hall",
          action: "Updated",
          section: "CONTACTS",
          item: "Contact: VIP Customer",
          description: "Updated VIP customer contact details",
          outcome: "Success",
          created_at: now.subtract(1, "month").subtract(10, "day").toISOString(),
        },
        {
          user_full_name: "Brian Allen",
          action: "Deleted",
          section: "AGENTS",
          item: "Agent: Deprecated Bot",
          description: "Removed deprecated agent from system",
          outcome: "Success",
          created_at: now.subtract(1, "month").subtract(20, "day").toISOString(),
        },
        {
          user_full_name: "Nicole Young",
          action: "Modified",
          section: "WORKSPACE",
          item: "Workspace Theme",
          description: "Changed workspace theme and branding",
          outcome: "Success",
          created_at: now.subtract(2, "month").subtract(5, "day").toISOString(),
        },
        {
          user_full_name: "Daniel King",
          action: "Created",
          section: "DATA",
          item: "Data: Customer Segment",
          description: "Created new customer segmentation",
          outcome: "Success",
          created_at: now.subtract(2, "month").subtract(10, "day").toISOString(),
        },
        {
          user_full_name: "Michelle Wright",
          action: "Updated",
          section: "DRIVES",
          item: "Drive: Retention Campaign",
          description: "Updated retention campaign settings",
          outcome: "Success",
          created_at: now.subtract(2, "month").subtract(20, "day").toISOString(),
        },
        {
          user_full_name: "Ryan Lopez",
          action: "Access",
          section: "AUTH",
          item: "User Session",
          description: "User logged in from mobile app",
          outcome: "Success",
          created_at: now.subtract(2, "month").subtract(25, "day").toISOString(),
        },
        // Additional items for pagination testing - Last year
        {
          user_full_name: "Jessica Hill",
          action: "Created",
          section: "USERS",
          item: "User: New Team Member",
          description: "Invited new team member to workspace",
          outcome: "Success",
          created_at: now.subtract(4, "month").toISOString(),
        },
        {
          user_full_name: "Brandon Scott",
          action: "Updated",
          section: "CONTACTS",
          item: "Contact: Enterprise Client",
          description: "Updated enterprise client information",
          outcome: "Success",
          created_at: now.subtract(5, "month").toISOString(),
        },
        {
          user_full_name: "Lauren Green",
          action: "Deleted",
          section: "DATA",
          item: "Data: Old Analytics",
          description: "Archived old analytics data",
          outcome: "Success",
          created_at: now.subtract(7, "month").toISOString(),
        },
        {
          user_full_name: "Justin Adams",
          action: "Modified",
          section: "AGENTS",
          item: "Agent: Support Assistant",
          description: "Updated support assistant configuration",
          outcome: "Success",
          created_at: now.subtract(9, "month").toISOString(),
        },
        {
          user_full_name: "Stephanie Baker",
          action: "Created",
          section: "CONVERSATIONS",
          item: "Conversation: Customer Feedback",
          description: "Started customer feedback conversation",
          outcome: "Success",
          created_at: now.subtract(11, "month").toISOString(),
        },
      ];

      // Client-side filtering and pagination for mock data
      let filteredData = [...mockActivities];

      // Apply search filter
      if (searchKeyword && searchKeyword.trim() !== "") {
        const searchLower = searchKeyword.toLowerCase();
        filteredData = filteredData.filter(
          (activity) =>
            (activity.user_full_name && activity.user_full_name.toLowerCase().includes(searchLower)) ||
            (activity.action && activity.action.toLowerCase().includes(searchLower)) ||
            (activity.section && activity.section.toLowerCase().includes(searchLower)) ||
            (activity.item && activity.item.toLowerCase().includes(searchLower)) ||
            (activity.description && activity.description.toLowerCase().includes(searchLower))
        );
      }

      // Apply action filter
      if (Array.isArray(params.actions) && params.actions.length > 0) {
        filteredData = filteredData.filter((activity) =>
          params.actions.includes(activity.action)
        );
      }

      // Apply section filter
      if (Array.isArray(params.sections) && params.sections.length > 0) {
        filteredData = filteredData.filter((activity) =>
          params.sections.includes(activity.section)
        );
      }

      // Apply modified (time range) filter
      if (params.modified) {
        const now = dayjs();
        let startDate;
        
        switch (params.modified) {
          case "Last 24 hours":
            startDate = now.subtract(24, "hour");
            break;
          case "Last 7 days":
            startDate = now.subtract(7, "day");
            break;
          case "Last 30 days":
            startDate = now.subtract(30, "day");
            break;
          case "Last 3 months":
            startDate = now.subtract(3, "month");
            break;
          case "Last year":
            startDate = now.subtract(1, "year");
            break;
          default:
            startDate = null;
        }
        
        if (startDate) {
          filteredData = filteredData.filter((activity) => {
            const activityDate = dayjs(activity.created_at || activity.timestamp);
            // Check if activity date is between startDate and now (inclusive)
            return (activityDate.isAfter(startDate) || activityDate.isSame(startDate)) && 
                   (activityDate.isBefore(now) || activityDate.isSame(now));
          });
        }
      }

      // Apply sorting
      const orderBy = mappedOrderBy;
      const orderDirectionStr = orderDirection;
      
      filteredData.sort((a, b) => {
        let aVal = a[orderBy];
        let bVal = b[orderBy];
        
        if (orderBy === "created_at" || orderBy === "timestamp") {
          aVal = dayjs(aVal).valueOf();
          bVal = dayjs(bVal).valueOf();
        } else {
          aVal = String(aVal || "").toLowerCase();
          bVal = String(bVal || "").toLowerCase();
        }
        
        if (orderDirectionStr === "ASC") {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });

      // Apply pagination
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = filteredData.slice(startIndex, endIndex);

      return {
        body: paginatedData,
        meta: {
          totalItems: filteredData.length,
          totalPages: Math.ceil(filteredData.length / pageSize),
          currentPage: page,
          perPage: pageSize,
          hasNext: endIndex < filteredData.length,
          hasPrevious: page > 1,
          nextCursor: null,
        },
      };
    } catch (mockError) {
      console.error("Mock data fetch failed:", mockError);
      throw new Error("Failed to fetch activity log data");
    }
  }
};

